import { periodCountFromSegment } from '../../helpers/matchTiming';
import { EventLightVm } from '../../utils/api';
import { buildScoreAtMomentByEventId } from './insightsMapData';
import {
    eventClock,
    eventLabel,
    eventMapPeriodKey,
    eventPrimaryMatchPlayerId,
    eventTeamId,
    formatPeriodLabel,
    MapPeriodKey,
    sortEventsChronological,
} from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import {
    ATTACK_LABELS,
    AttackSplit,
    AttackType,
    buildAttackSplits,
    conversionPercent,
    SideSplits,
} from './insightsTeamSplits';

/*
 * Single-match coaching panels derived from light events. Club perspective via ourTeamIds.
 */

export interface MatchSituationRow {
    type: AttackType;
    label: string;
    us: AttackSplit;
    them: AttackSplit;
    usPct?: number;
    themPct?: number;
}

export interface MatchSituationPanel {
    rows: MatchSituationRow[];
    /** Opponent EP conversion against us; PK% = 100 - this when defined. */
    opponentEpPct?: number;
    penaltyKillPct?: number;
}

export const buildMatchSituationPanel = (
    bundle: MatchBundle,
    ourTeamIds: number[]
): MatchSituationPanel => {
    const splits: SideSplits = buildAttackSplits([bundle], ourTeamIds);
    const order: AttackType[] = ['extraPlayer', 'penalty', 'counter', 'regular'];
    const rows = order.map((type) => ({
        type,
        label: ATTACK_LABELS[type],
        us: splits.us[type],
        them: splits.them[type],
        usPct: conversionPercent(splits.us[type]),
        themPct: conversionPercent(splits.them[type]),
    }));
    const opponentEpPct = conversionPercent(splits.them.extraPlayer);
    return {
        rows,
        opponentEpPct,
        penaltyKillPct: opponentEpPct == null ? undefined : 100 - opponentEpPct,
    };
};

export interface MatchPeriodDetail {
    periodKey: MapPeriodKey | 'other';
    label: string;
    periodId: number;
    goalsFor: number;
    goalsAgainst: number;
    shotsFor: number;
    shotsAgainst: number;
    exclusionsFor: number;
    exclusionsAgainst: number;
    turnoversFor: number;
    turnoversAgainst: number;
}

const emptyPeriod = (
    periodId: number,
    label: string,
    periodKey: MapPeriodKey | 'other'
): MatchPeriodDetail => ({
    periodKey,
    label,
    periodId,
    goalsFor: 0,
    goalsAgainst: 0,
    shotsFor: 0,
    shotsAgainst: 0,
    exclusionsFor: 0,
    exclusionsAgainst: 0,
    turnoversFor: 0,
    turnoversAgainst: 0,
});

/** Per-period box for one match (us vs them). */
export const buildMatchPeriodDetails = (
    bundle: MatchBundle,
    ourTeamIds: number[]
): MatchPeriodDetail[] => {
    const byId = new Map<number, MatchPeriodDetail>();

    const ensure = (event: EventLightVm): MatchPeriodDetail => {
        const existing = byId.get(event.periodId);
        if (existing) return existing;
        const periodCount = periodCountFromSegment(bundle.detail.matchSegmentId);
        const key = eventMapPeriodKey(event) || 'other';
        const label =
            formatPeriodLabel(event.period, event.periodId, periodCount) || `P${event.periodId}`;
        const row = emptyPeriod(event.periodId, label, key);
        byId.set(event.periodId, row);
        return row;
    };

    bundle.events.forEach((event: EventLightVm) => {
        if (event.shot) {
            const row = ensure(event);
            const ours = ourTeamIds.includes(event.shot.teamId);
            if (ours) {
                row.shotsFor += 1;
                if (event.shot.isGoal) row.goalsFor += 1;
            } else {
                row.shotsAgainst += 1;
                if (event.shot.isGoal) row.goalsAgainst += 1;
            }
            return;
        }
        if (event.exclusion) {
            const row = ensure(event);
            if (ourTeamIds.includes(event.exclusion.teamId)) row.exclusionsFor += 1;
            else row.exclusionsAgainst += 1;
            return;
        }
        if (event.turnover) {
            const row = ensure(event);
            if (ourTeamIds.includes(event.turnover.teamId)) row.turnoversFor += 1;
            else row.turnoversAgainst += 1;
        }
    });

    return Array.from(byId.values()).sort((a, b) => a.periodId - b.periodId);
};

export interface MatchNarrative {
    well: string;
    poorly: string;
    turningPoint: string;
}

/** Rule-based 3-line coach strip from splits + score path. */
export const buildMatchNarrative = (
    panel: MatchSituationPanel,
    periods: MatchPeriodDetail[],
    ourGoals: number,
    theirGoals: number
): MatchNarrative => {
    const best = [...panel.rows]
        .filter((r) => (r.us.shots || 0) >= 2)
        .sort((a, b) => (b.usPct ?? -1) - (a.usPct ?? -1))[0];
    const worst = [...panel.rows]
        .filter((r) => (r.us.shots || 0) >= 2)
        .sort((a, b) => (a.usPct ?? 999) - (b.usPct ?? 999))[0];

    const well = best
        ? `${best.label}: ${best.us.goals}/${best.us.shots}${
              best.usPct != null ? ` (${best.usPct}%)` : ''
          }`
        : panel.penaltyKillPct != null
        ? `Penalty kill ${panel.penaltyKillPct}%`
        : ourGoals >= theirGoals
        ? `Result ${ourGoals}:${theirGoals}`
        : `Logged ${ourGoals} goals`;

    const poorly =
        worst && worst !== best
            ? `${worst.label}: ${worst.us.goals}/${worst.us.shots}${
                  worst.usPct != null ? ` (${worst.usPct}%)` : ''
              }`
            : periods.length > 0
            ? (() => {
                  const soft = [...periods].sort(
                      (a, b) => a.goalsFor - a.goalsAgainst - (b.goalsFor - b.goalsAgainst)
                  )[0];
                  return soft
                      ? `${soft.label} differential ${
                            soft.goalsFor - soft.goalsAgainst > 0 ? '+' : ''
                        }${soft.goalsFor - soft.goalsAgainst}`
                      : 'No weak attack sample';
              })()
            : 'No weak attack sample';

    const swing = [...periods].sort(
        (a, b) => Math.abs(b.goalsFor - b.goalsAgainst) - Math.abs(a.goalsFor - a.goalsAgainst)
    )[0];
    const turningPoint = swing
        ? `${swing.label}: ${swing.goalsFor}:${swing.goalsAgainst}`
        : `Final ${ourGoals}:${theirGoals}`;

    return { well, poorly, turningPoint };
};

export interface TimelineRow {
    event: EventLightVm;
    periodLabel: string;
    clock: string;
    label: string;
    side: 'home' | 'away' | 'neutral';
    scoreAt?: string;
    playerLine?: string;
}

export interface TimelinePeriodGroup {
    periodLabel: string;
    periodId: number;
    rows: TimelineRow[];
}

/** Chronological timeline grouped by period; goals carry running score. */
export const buildMatchTimelineGroups = (
    bundle: MatchBundle,
    namesByMatchPlayerId: Map<number, string>
): TimelinePeriodGroup[] => {
    const { detail, events } = bundle;
    const scoreAt = buildScoreAtMomentByEventId(detail, events);
    const groups: TimelinePeriodGroup[] = [];
    let current: TimelinePeriodGroup | undefined;

    sortEventsChronological(events).forEach((event: EventLightVm) => {
        const periodLabel =
            formatPeriodLabel(event.period, event.periodId) || event.period || `P${event.periodId}`;
        if (!current || current.periodId !== event.periodId) {
            current = { periodLabel, periodId: event.periodId, rows: [] };
            groups.push(current);
        }

        const teamId = eventTeamId(event);
        const side =
            teamId === detail.homeTeamId
                ? 'home'
                : teamId === detail.awayTeamId
                ? 'away'
                : 'neutral';
        const mpId = eventPrimaryMatchPlayerId(event);
        const playerLine = mpId != null ? namesByMatchPlayerId.get(mpId) : undefined;
        current.rows.push({
            event,
            periodLabel,
            clock: eventClock(event),
            label: eventLabel(event),
            side,
            scoreAt: event.shot?.isGoal ? scoreAt.get(event.id) : undefined,
            playerLine,
        });
    });

    return groups;
};
